import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { type WritableDraft } from 'immer';
import { first, type Observable, of, switchMap } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type AggregateFunctionName } from '@blaze-shared/api-contracts-domain';

import { ElementsFacade } from '../../../+state/elements/elements.facade';
import { type EngineState } from '../../../+state/engine';
import { RuntimeFacade } from '../../../+state/runtime/runtime.facade';
import {
  AiWorkflowGenerationService,
  type ElementHandler,
  findElement,
  getElement,
  patchTitle,
  upsertElement,
} from '../../../ai';
import { type ElementContext } from '../../../ai/models/ai.model';
import { toWidthSettings } from '../../../ai/models/styles';

import {
  type RoundChartContext,
  type RoundChartResolvedContext,
  type RoundChartType,
} from './round-chart.definition';

export abstract class RoundChartElementHandler<T extends RoundChartType>
  implements ElementHandler<T>
{
  protected readonly runtimeFacade = inject(RuntimeFacade);
  protected readonly elementsFacade = inject(ElementsFacade);
  protected readonly aiWorkflowGenerationService = inject(AiWorkflowGenerationService);
  protected readonly store = inject(Store);

  abstract readonly type: T;

  prepare(context: RoundChartContext<T>): Observable<RoundChartResolvedContext<T>> {
    const workflowContext = context.componentWorkflows[context.element.id];

    if (!workflowContext) {
      return of(context);
    }

    return of({
      ...context,
      workflowContext,
    });
  }

  process(context: RoundChartResolvedContext<T>, draft: WritableDraft<EngineState>): void {
    const props = this.getChartProps(context, draft);

    const result = upsertElement(draft, context as ElementContext<T>, props);
    const element = getElement(draft, result.elementId, this.type);

    if (context.element.width) {
      element.width = toWidthSettings(context.element.width);
    } else {
      element.width = {
        mode: 'full',
      };
    }

    if (context.element.height) {
      element.height = {
        mode: 'value',
        value: context.element.height,
        fixed: true,
      };
    }
  }

  finalize(element: CanvasElementFrom<T>, context: RoundChartResolvedContext<T>): Observable<any> {
    if (!context.workflowContext) {
      return of(null);
    }

    const { workflow, inputVariables } = context.workflowContext;

    return this.aiWorkflowGenerationService.getWorkflowRelatedDetails(element.id).pipe(
      first(),
      switchMap(({ relatedDatasetMap, relatedDetails }) =>
        this.aiWorkflowGenerationService.finalizeAiWorkflowCreation(
          element.id,
          workflow,
          inputVariables,
          relatedDatasetMap,
          relatedDetails,
          'value',
        ),
      ),
    );
  }

  private getInitialProps(
    context: RoundChartResolvedContext<T>,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<RoundChartType> {
    const element = findElement<T>(draft, context.element.id);
    return (
      element?.props ?? {
        content: {
          chart: {
            showTotal: true,
          },
        },
      }
    );
  }

  private getChartProps(
    context: RoundChartResolvedContext<T>,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<RoundChartType> {
    const data = context.element.data;

    let props = this.getInitialProps(context, draft);

    if (context.element.title) {
      props = patchTitle<RoundChartType>(props, context.element.title, context.element.showTitle);
    }

    if (context.element.precision) {
      props = applyPropsPatch(props, {
        content: {
          format: {
            precision: context.element.precision,
          },
        },
      });
    }

    if (data?.$type) {
      props = applyPropsPatch(props, {
        content: {
          data: {
            aggregation: (data?.$type === 'value' ? 'FIELD VALUE' : data?.$type.toUpperCase()) as
              | AggregateFunctionName
              | 'FIELD VALUE',
          },
        },
      });
    }

    return props;
  }
}
